import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Sun, Moon, LogOut, Users, UserPlus, LayoutDashboard,
  Settings, HelpCircle, Menu, X, Search, Edit3,
  History, TrendingUp, Wallet, Target, ChevronDown,
  User, CreditCard, Lock, Phone, Mail, FileText,
  DollarSign, Eye, EyeOff, Shield, ArrowUpRight, ArrowDownRight, BarChart3,
  Upload, Download, FileJson, Check, AlertTriangle, Copy, CheckCircle, RotateCcw, Wrench,
  Calendar, Clock, Trash2, Plus, Banknote, Percent, Hash, Bell, Send, Power, Zap, Activity
} from 'lucide-react';
import { api } from '../api';
import './AdminPage.css';

const LOGO_URL = 'https://acesso.ihubs.app/api/uploads/logo/acesso/logo-light-1773085624457.png';

const formatCurrency = (value) =>
  Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const shortName = (name) => {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length <= 2) return name;
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

/* Donut chart component */
function DonutChart({ percent, size = 140, stroke = 12, color = '#3b82f6' }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const filled = (percent / 100) * circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="donut-svg">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--progress-bg)" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${filled} ${circ - filled}`} strokeDashoffset={circ * 0.25}
        strokeLinecap="round" style={{ transition: 'stroke-dasharray 0.8s ease' }} />
    </svg>
  );
}

/* Bar chart for top clients */
function MiniBar({ value, max, color = '#3b82f6' }) {
  const w = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="mini-bar">
      <div className="mini-bar-fill" style={{ width: `${w}%`, background: color }} />
    </div>
  );
}

function AdminPage({ user, onLogout, theme, setTheme, setPage }) {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [emailFilter, setEmailFilter] = useState('all'); // 'all' | 'with' | 'without'
  const [currentPage, setCurrentPage] = useState(1);
  const USERS_PER_PAGE = 20;
  const [modal, setModal] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    cpf: '', password: '', name: '', email: '', phone: '', role: 'usuario',
    description: '', goal: '', balance: '', deposit_due_day: '', credit_limit: '',
    installments: '', installment_value: '', nome_mae: '', dt_nascimento: '',
    allow_daily_withdrawal: false
  });

  // Email tool
  const [emailModal, setEmailModal] = useState(false);
  const [emailTab, setEmailTab] = useState('compose');
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState({ logs: [], total: 0 });
  const [emailLogsPage, setEmailLogsPage] = useState(1);
  const [emailLogsFilter, setEmailLogsFilter] = useState('');
  const [emailForm, setEmailForm] = useState({ subject: '', body_html: '', template_id: null, user_ids: [], mode: 'individual', user_id: '' });
  const [emailTemplateForm, setEmailTemplateForm] = useState({ name: '', subject: '', body_html: '', category: 'geral', editing: null });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [noDepositUsers, setNoDepositUsers] = useState([]);
  const [userEmailHistory, setUserEmailHistory] = useState({ userId: null, logs: [], show: false });
  const [emailStats, setEmailStats] = useState(null);

  const loadEmailStats = async () => {
    try { const r = await api.getEmailStats(); setEmailStats(r); } catch (e) {}
  };
  const loadEmailTemplates = async () => {
    try { const r = await api.getEmailTemplates(); setEmailTemplates(r); } catch (e) {}
  };
  const loadEmailLogs = async (page = 1, status = '') => {
    try {
      const params = { page, limit: 20 };
      if (status) params.status = status;
      const r = await api.getEmailLogs(params);
      setEmailLogs(r);
    } catch (e) {}
  };
  const loadNoDepositUsers = async () => {
    try { const r = await api.getNoDepositUsers(); setNoDepositUsers(r); } catch (e) {}
  };

  const openEmailModal = () => {
    setEmailModal(true);
    setEmailTab('compose');
    setEmailError('');
    setEmailSuccess('');
    setEmailForm({ subject: '', body_html: '', template_id: null, user_ids: [], mode: 'individual', user_id: '' });
    loadEmailTemplates();
    loadNoDepositUsers();
  };

  const handleSendEmail = async () => {
    setEmailError('');
    setEmailSuccess('');
    setEmailLoading(true);
    try {
      if (emailForm.mode === 'reminder') {
        const r = await api.sendReminders({ subject: emailForm.subject || undefined, body_html: emailForm.body_html || undefined, template_id: emailForm.template_id || undefined });
        setEmailSuccess(`${r.sent} lembrete(s) enviado(s)${r.failed > 0 ? `, ${r.failed} falha(s)` : ''}`);
      } else if (emailForm.mode === 'bulk') {
        if (!emailForm.user_ids.length) { setEmailError('Selecione ao menos um destinatário'); setEmailLoading(false); return; }
        const r = await api.sendBulkEmail({ user_ids: emailForm.user_ids, subject: emailForm.subject, body_html: emailForm.body_html, template_id: emailForm.template_id || undefined });
        setEmailSuccess(`${r.sent} e-mail(s) enviado(s)${r.failed > 0 ? `, ${r.failed} falha(s)` : ''}`);
      } else {
        if (!emailForm.user_id) { setEmailError('Selecione um destinatário'); setEmailLoading(false); return; }
        if (!emailForm.subject || !emailForm.body_html) { setEmailError('Assunto e corpo são obrigatórios'); setEmailLoading(false); return; }
        await api.sendEmail({ user_id: parseInt(emailForm.user_id), subject: emailForm.subject, body_html: emailForm.body_html, template_id: emailForm.template_id || undefined });
        setEmailSuccess('E-mail enviado com sucesso!');
      }
      loadNoDepositUsers();
    } catch (err) {
      setEmailError(err.error || 'Erro ao enviar');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    setEmailError('');
    setEmailLoading(true);
    try {
      if (emailTemplateForm.editing) {
        await api.updateEmailTemplate(emailTemplateForm.editing, emailTemplateForm);
      } else {
        await api.createEmailTemplate(emailTemplateForm);
      }
      setEmailTemplateForm({ name: '', subject: '', body_html: '', category: 'geral', editing: null });
      setEmailSuccess('Template salvo!');
      setTimeout(() => setEmailSuccess(''), 2000);
      loadEmailTemplates();
    } catch (err) {
      setEmailError(err.error || 'Erro ao salvar template');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try { await api.deleteEmailTemplate(id); loadEmailTemplates(); } catch (e) {}
  };

  const loadUserEmailHistory = async (userId) => {
    try {
      const logs = await api.getUserEmailLogs(userId);
      setUserEmailHistory({ userId, logs, show: true });
    } catch (e) {}
  };

  // CPF lookup
  const [cpfLoading, setCpfLoading] = useState(false);

  const handleCpfLookup = async (cpfValue) => {
    const cleanCpf = cpfValue.replace(/\D/g, '');
    if (cleanCpf.length !== 11) return;
    setCpfLoading(true);
    try {
      const result = await api.lookupCpf(cleanCpf);
      if (result.encontrado && result.dados) {
        const d = result.dados;
        setForm(prev => ({
          ...prev,
          name: d.NOME || prev.name,
          nome_mae: d.NOME_MAE || prev.nome_mae,
          dt_nascimento: d.DT_NASCIMENTO || prev.dt_nascimento,
          phone: d.CELULAR1 || d.TEL_FIXO1 || prev.phone,
          email: d.EMAIL || prev.email,
        }));
      }
    } catch (err) {
      // silently fail - user can fill manually
    } finally {
      setCpfLoading(false);
    }
  };

  // Loan modal
  const [loanModal, setLoanModal] = useState(null); // user object
  const [loans, setLoans] = useState([]);
  const [loanForm, setLoanForm] = useState({ total_amount: '', interest_rate: '0', num_installments: '', description: '' });
  const [loanLoading, setLoanLoading] = useState(false);
  const [loanError, setLoanError] = useState('');
  const [loanSuccess, setLoanSuccess] = useState('');

  // Schedule modal
  const [scheduleModal, setScheduleModal] = useState(null); // user object
  const [schedules, setSchedules] = useState([]);
  const [scheduleForm, setScheduleForm] = useState({ due_date: '', amount: '', description: '' });
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [scheduleSuccess, setScheduleSuccess] = useState('');

  // Maintenance mode
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);

  // Webhooks
  const [webhookModal, setWebhookModal] = useState(false);
  const [webhooks, setWebhooks] = useState([]);
  const [webhookForm, setWebhookForm] = useState({ name: '', url: '', events: ['deposit', 'withdraw', 'loan'] });
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookError, setWebhookError] = useState('');
  const [webhookSuccess, setWebhookSuccess] = useState('');

  // Import JSON state
  const [importModal, setImportModal] = useState(false);
  const [importMode, setImportMode] = useState('new'); // 'new' or 'existing'
  const [importUserId, setImportUserId] = useState('');
  const [importJson, setImportJson] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState('');
  const [importSuccess, setImportSuccess] = useState('');
  const [showModel, setShowModel] = useState(false);
  const [modelCopied, setModelCopied] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [u, s] = await Promise.all([api.getUsers(), api.getStats()]);
      setUsers(u);
      setStats(s);
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Load maintenance status
  useEffect(() => {
    if (user.role === 'admin') {
      api.getMaintenanceStatus()
        .then(data => setMaintenanceMode(data.maintenance))
        .catch(() => {});
    }
  }, [user.role]);

  const handleToggleMaintenance = async () => {
    const newState = !maintenanceMode;
    const action = newState ? 'ativar o modo de manutenção' : 'desativar o modo de manutenção';
    if (!window.confirm(`Deseja ${action}? ${newState ? 'Nenhum usuário conseguirá acessar a plataforma.' : 'Os usuários poderão acessar normalmente.'}`)) return;
    setMaintenanceLoading(true);
    try {
      await api.toggleMaintenance(newState);
      setMaintenanceMode(newState);
      setSuccess(newState ? 'Modo de manutenção ativado!' : 'Modo de manutenção desativado!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.error || 'Erro ao alterar modo de manutenção');
      setTimeout(() => setError(''), 3000);
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const openScheduleModal = async (u) => {
    setScheduleModal(u);
    setScheduleForm({ due_date: '', amount: '', description: '' });
    setScheduleError('');
    setScheduleSuccess('');
    try {
      const data = await api.getUserSchedules(u.id);
      setSchedules(data);
    } catch {
      setSchedules([]);
    }
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    setScheduleError('');
    if (!scheduleForm.due_date || !scheduleForm.amount) {
      setScheduleError('Data e valor são obrigatórios');
      return;
    }
    setScheduleLoading(true);
    try {
      await api.createSchedule(scheduleModal.id, scheduleForm);
      const data = await api.getUserSchedules(scheduleModal.id);
      setSchedules(data);
      setScheduleForm({ due_date: '', amount: '', description: '' });
      setScheduleSuccess('Vencimento adicionado!');
      setTimeout(() => setScheduleSuccess(''), 2000);
    } catch (err) {
      setScheduleError(err.error || 'Erro ao criar vencimento');
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleToggleScheduleStatus = async (s) => {
    const newStatus = s.status === 'pago' ? 'pendente' : 'pago';
    try {
      await api.updateSchedule(s.id, { status: newStatus });
      const data = await api.getUserSchedules(scheduleModal.id);
      setSchedules(data);
    } catch {}
  };

  const handleDeleteSchedule = async (s) => {
    if (!window.confirm('Remover este vencimento?')) return;
    try {
      await api.deleteSchedule(s.id);
      const data = await api.getUserSchedules(scheduleModal.id);
      setSchedules(data);
    } catch {}
  };

  // Loan handlers
  const openLoanModal = async (u) => {
    setLoanModal(u);
    setLoanForm({ total_amount: '', interest_rate: '0', num_installments: '', description: '' });
    setLoanError('');
    setLoanSuccess('');
    try {
      const data = await api.getUserLoans(u.id);
      setLoans(data);
    } catch {
      setLoans([]);
    }
  };

  const handleCreateLoan = async (e) => {
    e.preventDefault();
    setLoanError('');
    if (!loanForm.total_amount || !loanForm.num_installments) {
      setLoanError('Valor e número de parcelas são obrigatórios');
      return;
    }
    setLoanLoading(true);
    try {
      await api.createLoan(loanModal.id, {
        total_amount: parseFloat(loanForm.total_amount),
        interest_rate: parseFloat(loanForm.interest_rate || 0),
        num_installments: parseInt(loanForm.num_installments),
        description: loanForm.description || null
      });
      const data = await api.getUserLoans(loanModal.id);
      setLoans(data);
      setLoanForm({ total_amount: '', interest_rate: '0', num_installments: '', description: '' });
      setLoanSuccess('Empréstimo criado com sucesso!');
      setTimeout(() => setLoanSuccess(''), 3000);
    } catch (err) {
      setLoanError(err.error || 'Erro ao criar empréstimo');
    } finally {
      setLoanLoading(false);
    }
  };

  const handleToggleInstallment = async (inst) => {
    const newStatus = inst.status === 'pago' ? 'pendente' : 'pago';
    try {
      await api.updateInstallment(inst.id, { status: newStatus });
      const data = await api.getUserLoans(loanModal.id);
      setLoans(data);
    } catch {}
  };

  const handleDeleteLoan = async (loanId) => {
    if (!window.confirm('Remover este empréstimo e todas as parcelas?')) return;
    try {
      await api.deleteLoan(loanId);
      const data = await api.getUserLoans(loanModal.id);
      setLoans(data);
      setLoanSuccess('Empréstimo removido!');
      setTimeout(() => setLoanSuccess(''), 2000);
    } catch {}
  };

  const loanInstallmentPreview = (() => {
    const amt = parseFloat(loanForm.total_amount);
    const rate = parseFloat(loanForm.interest_rate || 0);
    const n = parseInt(loanForm.num_installments);
    if (!amt || !n || amt <= 0 || n <= 0) return null;
    const total = amt * (1 + rate / 100);
    const inst = total / n;
    return { total, installment: inst };
  })();

  // Webhook handlers
  const openWebhookModal = async () => {
    setWebhookModal(true);
    setWebhookForm({ name: '', url: '', events: ['deposit', 'withdraw', 'loan'] });
    setWebhookError('');
    setWebhookSuccess('');
    try { const data = await api.getWebhooks(); setWebhooks(data); } catch { setWebhooks([]); }
  };

  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    if (!webhookForm.name || !webhookForm.url) { setWebhookError('Nome e URL são obrigatórios'); return; }
    setWebhookLoading(true);
    try {
      await api.createWebhook(webhookForm);
      const data = await api.getWebhooks(); setWebhooks(data);
      setWebhookForm({ name: '', url: '', events: ['deposit', 'withdraw', 'loan'] });
      setWebhookSuccess('Webhook adicionado!'); setTimeout(() => setWebhookSuccess(''), 2000);
    } catch (err) { setWebhookError(err.error || 'Erro ao criar webhook'); }
    finally { setWebhookLoading(false); }
  };

  const handleToggleWebhook = async (wh) => {
    try { await api.updateWebhook(wh.id, { active: !wh.active }); const data = await api.getWebhooks(); setWebhooks(data); } catch {}
  };

  const handleDeleteWebhook = async (wh) => {
    if (!window.confirm(`Remover webhook "${wh.name}"?`)) return;
    try { await api.deleteWebhook(wh.id); const data = await api.getWebhooks(); setWebhooks(data); } catch {}
  };

  const handleTestWebhook = async (wh) => {
    try { await api.testWebhook(wh.id); setWebhookSuccess('Teste enviado!'); setTimeout(() => setWebhookSuccess(''), 2000); } catch {}
  };

  const toggleWebhookEvent = (evt) => {
    setWebhookForm(prev => ({
      ...prev,
      events: prev.events.includes(evt) ? prev.events.filter(e => e !== evt) : [...prev.events, evt]
    }));
  };

  const formatCpfInput = (value) => {
    const nums = value.replace(/\D/g, '').slice(0, 11);
    return nums.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const formatPhoneInput = (value) => {
    const nums = value.replace(/\D/g, '').slice(0, 11);
    if (nums.length <= 2) return nums;
    if (nums.length <= 7) return `(${nums.slice(0, 2)}) ${nums.slice(2)}`;
    return `(${nums.slice(0, 2)}) ${nums.slice(2, 7)}-${nums.slice(7)}`;
  };

  const openCreate = () => {
    setForm({ cpf: '', password: '', name: '', email: '', phone: '', role: 'usuario', description: '', goal: '', balance: '', deposit_due_day: '', credit_limit: '', installments: '', installment_value: '', nome_mae: '', dt_nascimento: '', allow_daily_withdrawal: false });
    setError('');
    setSuccess('');
    setModal('create');
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      cpf: u.cpf, password: '', name: u.name, email: u.email || '',
      phone: u.phone || '', role: u.role, description: u.description || '',
      goal: u.goal || '', balance: u.balance || '', deposit_due_day: u.deposit_due_day || '',
      credit_limit: u.credit_limit || '',
      installments: u.installments || '', installment_value: u.installment_value || '',
      nome_mae: u.nome_mae || '', dt_nascimento: u.dt_nascimento || '',
      allow_daily_withdrawal: u.allow_daily_withdrawal || false
    });
    setError('');
    setSuccess('');
    setModal('edit');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = {
        cpf: form.cpf.replace(/\D/g, ''), password: form.password, name: form.name,
        email: form.email, phone: form.phone || undefined, role: form.role,
        description: form.description || undefined,
        goal: form.goal ? parseFloat(form.goal) : 0,
        balance: form.balance ? parseFloat(form.balance) : 0,
        deposit_due_day: form.deposit_due_day ? parseInt(form.deposit_due_day) : null,
        credit_limit: form.credit_limit ? parseFloat(form.credit_limit) : 0,
        installments: form.installments ? parseInt(form.installments) : null,
        installment_value: form.installment_value ? parseFloat(form.installment_value) : null,
        nome_mae: form.nome_mae || null,
        dt_nascimento: form.dt_nascimento || null,
        allow_daily_withdrawal: form.allow_daily_withdrawal || false,
      };
      await api.createUser(data);
      setModal(null);
      setSuccess('Usuario criado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err) {
      setError(err.error || 'Erro ao criar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = {
        name: form.name, email: form.email, phone: form.phone || null,
        cpf: form.cpf.replace(/\D/g, ''),
        role: form.role, description: form.description || null,
        goal: form.goal ? parseFloat(form.goal) : 0,
        balance: form.balance ? parseFloat(form.balance) : 0,
        deposit_due_day: form.deposit_due_day ? parseInt(form.deposit_due_day) : null,
        credit_limit: form.credit_limit ? parseFloat(form.credit_limit) : 0,
        installments: form.installments ? parseInt(form.installments) : null,
        installment_value: form.installment_value ? parseFloat(form.installment_value) : null,
        nome_mae: form.nome_mae || null,
        dt_nascimento: form.dt_nascimento || null,
        allow_daily_withdrawal: form.allow_daily_withdrawal || false,
      };
      if (form.password && form.password.length >= 4) {
        data.password = form.password;
      }
      await api.updateUser(editUser.id, data);
      setModal(null);
      setSuccess('Usuario atualizado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err) {
      setError(err.error || 'Erro ao atualizar usuario');
    } finally {
      setLoading(false);
    }
  };

  // Import JSON handlers
  const jsonModel = {
    user: {
      cpf: "12345678901",
      password: "senha123",
      name: "João da Silva",
      email: "joao@email.com",
      phone: "11999999999",
      role: "usuario",
      description: "Observações sobre o cliente",
      goal: 50000.00
    },
    transactions: [
      { type: "deposit", amount: 1500.00, description: "Depósito mensal - Janeiro", date: "2025-01-15" },
      { type: "deposit", amount: 2000.00, description: "Depósito mensal - Fevereiro", date: "2025-02-10" },
      { type: "withdraw", amount: 500.00, description: "Saque mensal - Março", date: "2025-03-20" }
    ]
  };

  const transactionsOnlyModel = {
    transactions: [
      { type: "deposit", amount: 1500.00, description: "Depósito mensal - Janeiro", date: "2025-01-15" },
      { type: "deposit", amount: 2000.00, description: "Depósito mensal - Fevereiro", date: "2025-02-10" },
      { type: "withdraw", amount: 500.00, description: "Saque mensal - Março", date: "2025-03-20" }
    ]
  };

  const openImportModal = () => {
    setImportModal(true);
    setImportMode('new');
    setImportUserId('');
    setImportJson('');
    setImportError('');
    setImportSuccess('');
    setShowModel(false);
    setModelCopied(false);
  };

  const copyModel = () => {
    const model = importMode === 'new' ? jsonModel : transactionsOnlyModel;
    navigator.clipboard.writeText(JSON.stringify(model, null, 2));
    setModelCopied(true);
    setTimeout(() => setModelCopied(false), 2000);
  };

  const downloadModel = () => {
    const model = importMode === 'new' ? jsonModel : transactionsOnlyModel;
    const blob = new Blob([JSON.stringify(model, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = importMode === 'new' ? 'modelo-usuario-completo.json' : 'modelo-transacoes.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImportJson(ev.target.result);
      setImportError('');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    setImportError('');
    setImportSuccess('');

    let parsed;
    try {
      parsed = JSON.parse(importJson);
    } catch {
      setImportError('JSON inválido. Verifique o formato e tente novamente.');
      return;
    }

    setImportLoading(true);
    try {
      if (importMode === 'new') {
        if (!parsed.user) {
          setImportError('O JSON deve conter o campo "user" para criar um novo usuário.');
          setImportLoading(false);
          return;
        }
        const result = await api.importUserWithTransactions(parsed);
        setImportSuccess(result.message);
      } else {
        if (!importUserId) {
          setImportError('Selecione o usuário para importar as transações.');
          setImportLoading(false);
          return;
        }
        if (!parsed.transactions) {
          setImportError('O JSON deve conter o campo "transactions" com um array de transações.');
          setImportLoading(false);
          return;
        }
        const result = await api.importTransactions(importUserId, parsed);
        setImportSuccess(result.message);
      }
      await loadData();
      setTimeout(() => {
        setImportModal(false);
        setImportSuccess('');
        setSuccess(importMode === 'new' ? 'Usuário importado com sucesso!' : 'Transações importadas com sucesso!');
        setTimeout(() => setSuccess(''), 3000);
      }, 2000);
    } catch (err) {
      setImportError(err.error || 'Erro ao importar');
    } finally {
      setImportLoading(false);
    }
  };

  const handleToggleStatus = async (u) => {
    const newStatus = u.status === 'acordo_pago' ? 'ativo' : 'acordo_pago';
    const action = newStatus === 'acordo_pago' ? 'marcar como Acordo Pago' : 'reativar';
    if (!window.confirm(`Deseja ${action} o cliente "${u.name}"?`)) return;
    try {
      await api.updateUserStatus(u.id, newStatus);
      setSuccess(newStatus === 'acordo_pago' ? `${u.name} marcado como Acordo Pago!` : `${u.name} reativado!`);
      setTimeout(() => setSuccess(''), 3000);
      await loadData();
    } catch (err) {
      setError(err.error || 'Erro ao alterar status');
      setTimeout(() => setError(''), 3000);
    }
  };

  const filteredUsers = users.filter((u) => {
    // Email filter
    if (emailFilter === 'with' && (!u.email || !u.email.trim())) return false;
    if (emailFilter === 'without' && u.email && u.email.trim()) return false;
    // Search filter
    if (!search.trim()) return true;
    const searchLower = search.toLowerCase().trim();
    const searchDigits = search.replace(/\D/g, '');
    return u.name.toLowerCase().includes(searchLower) ||
      (searchDigits.length > 0 && u.cpf.includes(searchDigits));
  });

  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE) || 1;
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [search, emailFilter]);

  const initials = (user.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const isAdmin = user.role === 'admin';

  /* Chart data */
  const chartData = useMemo(() => {
    const totalBalance = users.reduce((s, u) => s + Number(u.balance || 0), 0);
    const totalGoal = users.reduce((s, u) => s + Number(u.goal || 0), 0);
    const overallProgress = totalGoal > 0 ? Math.min((totalBalance / totalGoal) * 100, 100) : 0;

    const topClients = [...users]
      .filter(u => u.role === 'usuario')
      .map(u => ({
        ...u,
        progress: Number(u.goal) > 0 ? Math.min((Number(u.balance) / Number(u.goal)) * 100, 100) : 0
      }))
      .sort((a, b) => b.progress - a.progress)
      .slice(0, 5);

    const roleCount = { usuario: 0, supervisor: 0, admin: 0 };
    users.forEach(u => { if (roleCount[u.role] !== undefined) roleCount[u.role]++; });
    const totalForRoles = users.length || 1;

    // Monthly deposit forecast - group by deposit_due_day
    const activeUsers = users.filter(u => u.role === 'usuario' && u.status !== 'acordo_pago');
    const totalMonthlyExpected = activeUsers.reduce((s, u) => s + Number(u.installment_value || 0), 0);
    const depositsByDay = {};
    activeUsers.forEach(u => {
      if (u.deposit_due_day && Number(u.installment_value || 0) > 0) {
        const day = u.deposit_due_day;
        if (!depositsByDay[day]) depositsByDay[day] = { day, total: 0, clients: [] };
        depositsByDay[day].total += Number(u.installment_value);
        depositsByDay[day].clients.push({ id: u.id, name: u.name, value: Number(u.installment_value) });
      }
    });
    const depositDays = Object.values(depositsByDay).sort((a, b) => a.day - b.day);

    // Email stats
    const clientUsers = users.filter(u => u.role === 'usuario');
    const withEmail = clientUsers.filter(u => u.email && u.email.trim()).length;
    const withoutEmail = clientUsers.length - withEmail;

    return { totalBalance, totalGoal, overallProgress, topClients, roleCount, totalForRoles, totalMonthlyExpected, depositDays, withEmail, withoutEmail, totalClients: clientUsers.length };
  }, [users]);

  return (
    <div className={`dashboard-container ${theme}`}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-top">
          <div className="sidebar-logo">
            <img src={LOGO_URL} alt="Logo" className="sidebar-logo-img" />
          </div>
          <nav className="sidebar-nav">
            <a href="#dashboard" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('dashboard'); }}>
              <LayoutDashboard size={19} /><span>Dashboard</span>
            </a>
            <a href="#admin" className="nav-item active" onClick={() => setSidebarOpen(false)}>
              <Users size={19} /><span>Admin</span>
            </a>
            <a href="#emails" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('emails'); }}>
              <Mail size={19} /><span>E-mails</span>
            </a>
            <a href="#automation" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('automation'); }}>
              <Zap size={19} /><span>Automacao IA</span>
            </a>
            <a href="#activity" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('activity'); }}>
              <Activity size={19} /><span>Atividade</span>
            </a>
            <a href="#history" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('history'); }}>
              <History size={19} /><span>Histórico</span>
            </a>
            <a href="#settings" className="nav-item" onClick={(e) => { e.preventDefault(); setPage('settings'); }}>
              <Settings size={19} /><span>Configurações</span>
            </a>
            <a href="#help" className="nav-item">
              <HelpCircle size={19} /><span>Ajuda</span>
            </a>
          </nav>
        </div>
        <div className="sidebar-bottom">
          <button className="sidebar-theme-btn" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            <span>{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>
          </button>
          <div className="sidebar-user">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <span className="user-name">{user.name}</span>
              <span className="user-role">{user.role}</span>
            </div>
            <button className="logout-btn-sidebar" onClick={onLogout} title="Sair"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>

      <div className="main-wrapper">
        <header className="topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>
          <img src={LOGO_URL} alt="Logo" className="topbar-logo" />
          <div className="topbar-right">
            <button className="topbar-theme" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <div className="user-avatar small">{initials}</div>
          </div>
        </header>

        <main className="dash-main">
          <div className="page-header">
            <div>
              <p className="page-greeting">Painel Administrativo</p>
              <h1 className="page-title">Gerenciamento de Clientes</h1>
            </div>
            <div className="page-actions">
              {isAdmin && (
                <button
                  className={`action-btn ${maintenanceMode ? 'maintenance-on' : 'maintenance-off'}`}
                  onClick={handleToggleMaintenance}
                  disabled={maintenanceLoading}
                  title={maintenanceMode ? 'Desativar manutenção' : 'Ativar manutenção'}
                >
                  <Wrench size={17} />
                  <span>{maintenanceLoading ? 'Aguarde...' : maintenanceMode ? 'Desativar Manutenção' : 'Manutenção'}</span>
                </button>
              )}
              {isAdmin && (
                <button className="action-btn" style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.2)' }}
                  onClick={openWebhookModal}>
                  <Bell size={17} /><span>Webhooks</span>
                </button>
              )}
              <button className="action-btn withdraw" onClick={openImportModal}>
                <Upload size={17} /><span>Importar JSON</span>
              </button>
              <button className="action-btn deposit" onClick={openCreate}>
                <UserPlus size={17} /><span>Novo Usuario</span>
              </button>
              <button className="action-btn" onClick={() => setPage('emails')} style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.2)' }}>
                <Mail size={17} /><span>E-mails</span>
              </button>
            </div>
          </div>

          {success && <div className="success-banner">{success}</div>}

          {/* Stats Cards */}
          {stats && (
            <>
              <div className="stats-grid four">
                <div className="stat-card">
                  <div className="stat-icon blue"><Users size={18} /></div>
                  <div className="stat-content">
                    <span className="stat-label">Total de clientes</span>
                    <span className="stat-value">{stats.totalUsers}</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon green"><Wallet size={18} /></div>
                  <div className="stat-content">
                    <span className="stat-label">Saldo total</span>
                    <span className="stat-value">{formatCurrency(stats.totalBalance)}</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon orange"><Target size={18} /></div>
                  <div className="stat-content">
                    <span className="stat-label">Meta total</span>
                    <span className="stat-value">{formatCurrency(stats.totalGoal)}</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon blue"><TrendingUp size={18} /></div>
                  <div className="stat-content">
                    <span className="stat-label">Progresso medio</span>
                    <span className="stat-value">{stats.averageProgress}%</span>
                  </div>
                </div>
              </div>

              {/* Loan Stats Cards */}
              {stats.totalLoans > 0 && (
                <div className="stats-grid four" style={{ marginTop: 12 }}>
                  <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }}><Banknote size={18} /></div>
                    <div className="stat-content">
                      <span className="stat-label">Empréstimos ativos</span>
                      <span className="stat-value">{stats.activeLoans}{stats.overdueLoans > 0 && <span style={{ fontSize: 12, color: '#ef4444', marginLeft: 4 }}>({stats.overdueLoans} atrasado{stats.overdueLoans !== 1 ? 's' : ''})</span>}</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon" style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }}><DollarSign size={18} /></div>
                    <div className="stat-content">
                      <span className="stat-label">Total emprestado</span>
                      <span className="stat-value">{formatCurrency(stats.totalLent)}</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon green"><ArrowDownRight size={18} /></div>
                    <div className="stat-content">
                      <span className="stat-label">Total recebido</span>
                      <span className="stat-value">{formatCurrency(stats.totalReceived)}</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-icon" style={{ background: stats.overdueInstallments > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', color: stats.overdueInstallments > 0 ? '#ef4444' : '#22c55e' }}>
                      <AlertTriangle size={18} />
                    </div>
                    <div className="stat-content">
                      <span className="stat-label">Parcelas atrasadas</span>
                      <span className="stat-value">{stats.overdueInstallments}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Charts Row */}
          <div className="charts-row">
            {/* Donut - Progresso geral */}
            <div className="chart-card">
              <div className="chart-card-header">
                <h3 className="chart-title"><Target size={16} /> Progresso Geral</h3>
              </div>
              <div className="donut-wrap">
                <DonutChart percent={chartData.overallProgress} size={160} stroke={14} />
                <div className="donut-center">
                  <span className="donut-percent">{chartData.overallProgress.toFixed(1)}%</span>
                  <span className="donut-label">concluido</span>
                </div>
              </div>
              <div className="donut-legend">
                <div className="legend-item">
                  <span className="legend-dot blue" />
                  <span className="legend-text">Acumulado</span>
                  <span className="legend-value">{formatCurrency(chartData.totalBalance)}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-dot gray" />
                  <span className="legend-text">Restante</span>
                  <span className="legend-value">{formatCurrency(Math.max(chartData.totalGoal - chartData.totalBalance, 0))}</span>
                </div>
              </div>
            </div>

            {/* Top clients progress */}
            <div className="chart-card">
              <div className="chart-card-header">
                <h3 className="chart-title"><BarChart3 size={16} /> Top Clientes</h3>
              </div>
              <div className="top-clients-chart">
                {chartData.topClients.length === 0 ? (
                  <div className="empty-chart"><Users size={24} className="empty-icon" /><p>Nenhum cliente</p></div>
                ) : (
                  chartData.topClients.map((c) => (
                    <div key={c.id} className="top-client-row">
                      <div className="top-client-info">
                        <div className="top-client-avatar">
                          {(c.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <span className="top-client-name">{shortName(c.name)}</span>
                      </div>
                      <div className="top-client-bar-wrap">
                        <MiniBar value={c.progress} max={100} color={c.progress >= 75 ? '#22c55e' : c.progress >= 40 ? '#3b82f6' : '#f59e0b'} />
                        <span className="top-client-pct">{c.progress.toFixed(0)}%</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Distribution card */}
            <div className="chart-card chart-card-sm">
              <div className="chart-card-header">
                <h3 className="chart-title"><Shield size={16} /> Distribuicao</h3>
              </div>
              <div className="dist-list">
                <div className="dist-item">
                  <div className="dist-icon-wrap blue"><User size={14} /></div>
                  <div className="dist-info">
                    <span className="dist-name">Usuarios</span>
                    <div className="dist-bar-row">
                      <div className="dist-bar"><div className="dist-bar-fill blue" style={{ width: `${(chartData.roleCount.usuario / chartData.totalForRoles) * 100}%` }} /></div>
                      <span className="dist-count">{chartData.roleCount.usuario}</span>
                    </div>
                  </div>
                </div>
                <div className="dist-item">
                  <div className="dist-icon-wrap orange"><Eye size={14} /></div>
                  <div className="dist-info">
                    <span className="dist-name">Supervisores</span>
                    <div className="dist-bar-row">
                      <div className="dist-bar"><div className="dist-bar-fill orange" style={{ width: `${(chartData.roleCount.supervisor / chartData.totalForRoles) * 100}%` }} /></div>
                      <span className="dist-count">{chartData.roleCount.supervisor}</span>
                    </div>
                  </div>
                </div>
                <div className="dist-item">
                  <div className="dist-icon-wrap purple"><Shield size={14} /></div>
                  <div className="dist-info">
                    <span className="dist-name">Admins</span>
                    <div className="dist-bar-row">
                      <div className="dist-bar"><div className="dist-bar-fill purple" style={{ width: `${(chartData.roleCount.admin / chartData.totalForRoles) * 100}%` }} /></div>
                      <span className="dist-count">{chartData.roleCount.admin}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Deposit Forecast */}
          {chartData.depositDays.length > 0 && (
            <div className="chart-card" style={{ marginBottom: '1.5rem' }}>
              <div className="chart-card-header">
                <h3 className="chart-title"><Calendar size={16} /> Previsão de depósitos mensais</h3>
                <span className="chart-subtitle" style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                  Total esperado: <strong style={{ color: '#22c55e' }}>{formatCurrency(chartData.totalMonthlyExpected)}</strong>/mês
                </span>
              </div>
              <div className="deposit-forecast-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem 0' }}>
                {chartData.depositDays.map((d) => {
                  const pct = chartData.totalMonthlyExpected > 0 ? (d.total / chartData.totalMonthlyExpected) * 100 : 0;
                  const today = new Date().getDate();
                  const isPast = d.day < today;
                  const isToday = d.day === today;
                  return (
                    <div key={d.day} style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem',
                      borderRadius: '8px', background: isToday ? 'rgba(59,130,246,0.08)' : 'transparent',
                      border: isToday ? '1px solid rgba(59,130,246,0.2)' : '1px solid transparent',
                      opacity: isPast ? 0.5 : 1
                    }}>
                      <div style={{
                        minWidth: '42px', height: '42px', borderRadius: '10px', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        background: isToday ? 'rgba(59,130,246,0.15)' : 'rgba(100,100,100,0.1)',
                        color: isToday ? '#3b82f6' : 'inherit', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.1
                      }}>
                        <span style={{ fontSize: '0.6rem', fontWeight: 600, textTransform: 'uppercase', opacity: 0.7 }}>Dia</span>
                        {d.day}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            {d.clients.length} cliente{d.clients.length !== 1 ? 's' : ''}
                            {isToday && <span style={{ color: '#3b82f6', marginLeft: '6px', fontSize: '0.7rem' }}>HOJE</span>}
                            {isPast && <span style={{ color: '#ef4444', marginLeft: '6px', fontSize: '0.7rem' }}>PASSOU</span>}
                          </span>
                          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#22c55e' }}>{formatCurrency(d.total)}</span>
                        </div>
                        <div style={{ height: '4px', borderRadius: '2px', background: 'rgba(100,100,100,0.15)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: '2px', width: `${pct}%`, background: isToday ? '#3b82f6' : '#22c55e', transition: 'width 0.3s' }} />
                        </div>
                        <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {d.clients.map(c => c.name.split(' ')[0]).join(', ')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Email Status Card */}
          <div className="chart-card" style={{ marginBottom: '1.5rem' }}>
            <div className="chart-card-header">
              <h3 className="chart-title"><Mail size={16} /> Status de e-mail dos clientes</h3>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', padding: '0.75rem 0', flexWrap: 'wrap' }}>
              <button onClick={() => setEmailFilter(emailFilter === 'all' ? 'all' : 'all')} style={{
                flex: 1, minWidth: '140px', padding: '14px 16px', borderRadius: '10px', border: emailFilter === 'all' ? '2px solid #3b82f6' : '1px solid rgba(100,100,100,0.2)',
                background: emailFilter === 'all' ? 'rgba(59,130,246,0.08)' : 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Users size={16} style={{ opacity: 0.7 }} />
                    <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Todos</span>
                  </div>
                  <span style={{ fontSize: '1.3rem', fontWeight: 800, color: '#3b82f6' }}>{chartData.totalClients}</span>
                </div>
              </button>
              <button onClick={() => setEmailFilter(emailFilter === 'with' ? 'all' : 'with')} style={{
                flex: 1, minWidth: '140px', padding: '14px 16px', borderRadius: '10px', border: emailFilter === 'with' ? '2px solid #22c55e' : '1px solid rgba(100,100,100,0.2)',
                background: emailFilter === 'with' ? 'rgba(34,197,94,0.08)' : 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Check size={16} style={{ color: '#22c55e' }} />
                    <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Com e-mail</span>
                  </div>
                  <span style={{ fontSize: '1.3rem', fontWeight: 800, color: '#22c55e' }}>{chartData.withEmail}</span>
                </div>
                <div style={{ marginTop: '8px', height: '4px', borderRadius: '2px', background: 'rgba(100,100,100,0.15)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: '2px', width: `${chartData.totalClients > 0 ? (chartData.withEmail / chartData.totalClients) * 100 : 0}%`, background: '#22c55e', transition: 'width 0.3s' }} />
                </div>
              </button>
              <button onClick={() => setEmailFilter(emailFilter === 'without' ? 'all' : 'without')} style={{
                flex: 1, minWidth: '140px', padding: '14px 16px', borderRadius: '10px', border: emailFilter === 'without' ? '2px solid #ef4444' : '1px solid rgba(100,100,100,0.2)',
                background: emailFilter === 'without' ? 'rgba(239,68,68,0.08)' : 'transparent', cursor: 'pointer', textAlign: 'left', transition: 'all 0.2s'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertTriangle size={16} style={{ color: '#ef4444' }} />
                    <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Sem e-mail</span>
                  </div>
                  <span style={{ fontSize: '1.3rem', fontWeight: 800, color: '#ef4444' }}>{chartData.withoutEmail}</span>
                </div>
                <div style={{ marginTop: '8px', height: '4px', borderRadius: '2px', background: 'rgba(100,100,100,0.15)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: '2px', width: `${chartData.totalClients > 0 ? (chartData.withoutEmail / chartData.totalClients) * 100 : 0}%`, background: '#ef4444', transition: 'width 0.3s' }} />
                </div>
              </button>
            </div>
            {emailFilter !== 'all' && (
              <div style={{ padding: '6px 0 4px', fontSize: '0.75rem', opacity: 0.7, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>Filtrando: {emailFilter === 'with' ? 'clientes com e-mail' : 'clientes sem e-mail'}</span>
                <button onClick={() => setEmailFilter('all')} style={{ background: 'rgba(100,100,100,0.15)', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '0.7rem', color: 'inherit' }}>Limpar</button>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="admin-search">
            <Search size={16} className="search-icon" />
            <input
              type="text" placeholder="Buscar por nome ou CPF..."
              value={search} onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Users Table - simplified */}
          <div className="history-card clients-card">
            <div className="history-card-header">
              <h3 className="section-title"><Users size={17} /> Clientes cadastrados</h3>
              <span className="history-count">{filteredUsers.length} usuario{filteredUsers.length !== 1 && 's'}</span>
            </div>
            {filteredUsers.length === 0 ? (
              <div className="empty-history"><Users size={32} className="empty-icon" /><p>Nenhum usuario encontrado</p></div>
            ) : (
              <div className="clients-list">
                {paginatedUsers.map((u) => {
                  const prog = Number(u.goal) > 0 ? Math.min((Number(u.balance) / Number(u.goal)) * 100, 100) : 0;
                  return (
                    <div key={u.id} className="client-row">
                      <div className="client-left">
                        <div className="client-avatar">{(u.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}</div>
                        <div className="client-info">
                          <span className="client-name">{shortName(u.name)}</span>
                          <span className="client-sub">
                            {u.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                            {u.deposit_due_day && <> · Venc. dia {u.deposit_due_day}</>}
                          </span>
                        </div>
                        <span className={`role-badge ${u.role}`}>{u.role}</span>
                        {u.status === 'acordo_pago' && (
                          <span className="role-badge acordo-pago"><CheckCircle size={12} /> Acordo Pago</span>
                        )}
                        {stats && stats.clientsWithLoans && stats.clientsWithLoans.includes(u.id) && (
                          <span className="role-badge" style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.2)' }}>
                            <Banknote size={12} /> Empréstimo
                          </span>
                        )}
                      </div>
                      <div className="client-right">
                        <div className="client-amounts">
                          <div className="client-amount-item">
                            <span className="client-amount-label">Saldo</span>
                            <span className="client-amount-val">{formatCurrency(u.balance)}</span>
                          </div>
                          <div className="client-amount-item">
                            <span className="client-amount-label">Meta</span>
                            <span className="client-amount-val">{formatCurrency(u.goal)}</span>
                          </div>
                        </div>
                        <div className="client-progress-wrap">
                          <div className="client-progress-bar">
                            <div className="client-progress-fill" style={{
                              width: `${prog}%`,
                              background: u.status === 'acordo_pago' ? '#22c55e' : prog >= 75 ? '#22c55e' : prog >= 40 ? '#3b82f6' : '#f59e0b'
                            }} />
                          </div>
                          <span className="client-progress-pct">{u.status === 'acordo_pago' ? 'Pago' : `${prog.toFixed(0)}%`}</span>
                        </div>
                        <button className="table-edit-btn" onClick={() => handleToggleStatus(u)}
                          title={u.status === 'acordo_pago' ? 'Reativar cliente' : 'Marcar como Acordo Pago'}
                          style={{ color: u.status === 'acordo_pago' ? '#f59e0b' : '#22c55e' }}>
                          {u.status === 'acordo_pago' ? <RotateCcw size={14} /> : <CheckCircle size={14} />}
                        </button>
                        <button className="table-edit-btn" onClick={() => openLoanModal(u)} title="Empréstimos" style={{ color: '#8b5cf6' }}>
                          <Banknote size={14} />
                        </button>
                        <button className="table-edit-btn" onClick={() => openScheduleModal(u)} title="Vencimentos">
                          <Calendar size={14} />
                        </button>
                        <button className="table-edit-btn" onClick={() => loadUserEmailHistory(u.id)} title="Histórico de e-mails" style={{ color: '#8b5cf6' }}>
                          <Mail size={14} />
                        </button>
                        <button className="table-edit-btn" onClick={() => openEdit(u)}>
                          <Edit3 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '16px 0 8px', flexWrap: 'wrap' }}>
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(100,100,100,0.2)', background: 'transparent', cursor: currentPage === 1 ? 'default' : 'pointer', opacity: currentPage === 1 ? 0.4 : 1, fontSize: '0.75rem', color: 'inherit' }}>
                  {'<<'}
                </button>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(100,100,100,0.2)', background: 'transparent', cursor: currentPage === 1 ? 'default' : 'pointer', opacity: currentPage === 1 ? 0.4 : 1, fontSize: '0.75rem', color: 'inherit' }}>
                  {'<'}
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .reduce((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => p === '...' ? (
                    <span key={`dots-${i}`} style={{ padding: '0 4px', opacity: 0.5, fontSize: '0.75rem' }}>...</span>
                  ) : (
                    <button key={p} onClick={() => setCurrentPage(p)}
                      style={{
                        padding: '6px 11px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: currentPage === p ? 700 : 400,
                        border: currentPage === p ? '2px solid #3b82f6' : '1px solid rgba(100,100,100,0.2)',
                        background: currentPage === p ? 'rgba(59,130,246,0.1)' : 'transparent',
                        color: currentPage === p ? '#3b82f6' : 'inherit', cursor: 'pointer'
                      }}>
                      {p}
                    </button>
                  ))
                }
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(100,100,100,0.2)', background: 'transparent', cursor: currentPage === totalPages ? 'default' : 'pointer', opacity: currentPage === totalPages ? 0.4 : 1, fontSize: '0.75rem', color: 'inherit' }}>
                  {'>'}
                </button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(100,100,100,0.2)', background: 'transparent', cursor: currentPage === totalPages ? 'default' : 'pointer', opacity: currentPage === totalPages ? 0.4 : 1, fontSize: '0.75rem', color: 'inherit' }}>
                  {'>>'}
                </button>
                <span style={{ fontSize: '0.72rem', opacity: 0.6, marginLeft: '8px' }}>
                  {(currentPage - 1) * USERS_PER_PAGE + 1}-{Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)} de {filteredUsers.length}
                </span>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Create/Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => { setModal(null); setError(''); setShowPassword(false); }}>
          <div className="modal-card modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-line" />
            <div className="modal-header">
              <div>
                <h3>{modal === 'create' ? 'Novo Usuario' : 'Editar Usuario'}</h3>
                <p className="modal-subtitle">
                  {modal === 'create' ? 'Preencha os dados para cadastrar um novo cliente' : `Editando: ${editUser?.name}`}
                </p>
              </div>
              <button className="modal-close" onClick={() => { setModal(null); setError(''); setShowPassword(false); }}><X size={18} /></button>
            </div>

            <form onSubmit={modal === 'create' ? handleCreate : handleEdit} className="modal-form">
              {/* Secao: Dados pessoais */}
              <div className="form-section">
                <div className="form-section-title">
                  <User size={15} />
                  <span>Dados pessoais</span>
                </div>

                <div className="form-row">
                  <div className="modal-input-group">
                    <label>Nome completo *</label>
                    <div className="input-with-icon">
                      <User size={16} className="field-icon" />
                      <input type="text" className="modal-input-styled" placeholder="Ex: Joao da Silva" required
                        value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </div>
                  </div>
                  <div className="modal-input-group">
                    <label>CPF * {cpfLoading && <span className="optional-tag">buscando...</span>}</label>
                    <div className="input-with-icon">
                      <CreditCard size={16} className="field-icon" />
                      <input type="text" className="modal-input-styled" placeholder="000.000.000-00" required
                        value={formatCpfInput(form.cpf)}
                        onChange={(e) => {
                          const val = e.target.value;
                          setForm({ ...form, cpf: val });
                          const clean = val.replace(/\D/g, '');
                          if (clean.length === 11 && modal === 'create') {
                            handleCpfLookup(val);
                          }
                        }} />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="modal-input-group">
                    <label>Telefone <span className="optional-tag">opcional</span></label>
                    <div className="input-with-icon">
                      <Phone size={16} className="field-icon" />
                      <input type="text" className="modal-input-styled" placeholder="(00) 00000-0000"
                        value={formatPhoneInput(form.phone)}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </div>
                  </div>
                  <div className="modal-input-group">
                    <label>E-mail *</label>
                    <div className="input-with-icon">
                      <Mail size={16} className="field-icon" />
                      <input type="email" className="modal-input-styled" placeholder="email@exemplo.com" required
                        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="modal-input-group">
                    <label>Nome da mãe <span className="optional-tag">opcional</span></label>
                    <div className="input-with-icon">
                      <User size={16} className="field-icon" />
                      <input type="text" className="modal-input-styled" placeholder="Ex: Maria da Silva"
                        value={form.nome_mae} onChange={(e) => setForm({ ...form, nome_mae: e.target.value })} />
                    </div>
                  </div>
                  <div className="modal-input-group">
                    <label>Data de nascimento <span className="optional-tag">opcional</span></label>
                    <div className="input-with-icon">
                      <Calendar size={16} className="field-icon" />
                      <input type="text" className="modal-input-styled" placeholder="DD/MM/AAAA"
                        value={form.dt_nascimento} onChange={(e) => setForm({ ...form, dt_nascimento: e.target.value })} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Secao: Acesso */}
              <div className="form-section">
                <div className="form-section-title">
                  <Shield size={15} />
                  <span>Acesso e permissoes</span>
                </div>

                <div className="modal-input-group">
                  <label>{modal === 'create' ? 'Senha *' : 'Nova senha'} {modal === 'edit' && <span className="optional-tag">deixe vazio para manter</span>}</label>
                  <div className="input-with-icon">
                    <Lock size={16} className="field-icon" />
                    <input type={showPassword ? 'text' : 'password'} className="modal-input-styled" placeholder="Minimo 4 caracteres"
                      required={modal === 'create'} minLength={modal === 'create' ? 4 : undefined}
                      value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                    <button type="button" className="field-toggle" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div className="modal-input-group">
                  <label>Tipo de usuario</label>
                  <div className="role-selector">
                    <button type="button" className={`role-option ${form.role === 'usuario' ? 'active' : ''}`}
                      onClick={() => setForm({ ...form, role: 'usuario' })}>
                      <User size={15} />
                      <div>
                        <span className="role-option-title">Usuario</span>
                        <span className="role-option-desc">Acesso basico</span>
                      </div>
                    </button>
                    <button type="button" className={`role-option ${form.role === 'supervisor' ? 'active' : ''}`}
                      onClick={() => setForm({ ...form, role: 'supervisor' })}>
                      <Eye size={15} />
                      <div>
                        <span className="role-option-title">Supervisor</span>
                        <span className="role-option-desc">Gerencia usuarios</span>
                      </div>
                    </button>
                    {isAdmin && (
                      <button type="button" className={`role-option ${form.role === 'admin' ? 'active' : ''}`}
                        onClick={() => setForm({ ...form, role: 'admin' })}>
                        <Shield size={15} />
                        <div>
                          <span className="role-option-title">Admin</span>
                          <span className="role-option-desc">Acesso total</span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Secao: Financeiro */}
              <div className="form-section">
                <div className="form-section-title">
                  <DollarSign size={15} />
                  <span>Dados financeiros</span>
                </div>

                <div className="form-row">
                  <div className="modal-input-group">
                    <label>Total de parcelas</label>
                    <div className="input-with-icon">
                      <Target size={16} className="field-icon" />
                      <input type="number" min="1" step="1" className="modal-input-styled" placeholder="Ex: 10"
                        value={form.installments} onChange={(e) => {
                          const installments = e.target.value;
                          const goal = installments && form.installment_value ? (parseFloat(installments) * parseFloat(form.installment_value)).toFixed(2) : '';
                          setForm({ ...form, installments, goal });
                        }} />
                    </div>
                  </div>
                  <div className="modal-input-group">
                    <label>Valor da parcela (R$)</label>
                    <div className="input-with-icon">
                      <DollarSign size={16} className="field-icon" />
                      <input type="number" step="0.01" min="0" className="modal-input-styled" placeholder="0,00"
                        value={form.installment_value} onChange={(e) => {
                          const installment_value = e.target.value;
                          const goal = form.installments && installment_value ? (parseFloat(form.installments) * parseFloat(installment_value)).toFixed(2) : '';
                          setForm({ ...form, installment_value, goal });
                        }} />
                    </div>
                  </div>
                </div>
                {form.installments && form.installment_value && (
                  <div className="form-row">
                    <div className="modal-input-group" style={{ width: '100%' }}>
                      <label>Meta de quitação (R$) <span className="optional-tag">calculado automaticamente</span></label>
                      <div className="input-with-icon">
                        <Target size={16} className="field-icon" />
                        <input type="text" className="modal-input-styled" readOnly
                          value={`R$ ${parseFloat(form.goal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (${form.installments}x de R$ ${parseFloat(form.installment_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`} />
                      </div>
                    </div>
                  </div>
                )}
                <div className="form-row">
                  <div className="modal-input-group">
                    <label>Saldo inicial (R$)</label>
                    <div className="input-with-icon">
                      <Wallet size={16} className="field-icon" />
                      <input type="number" step="0.01" min="0" className="modal-input-styled" placeholder="0,00"
                        value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="modal-input-group">
                    <label>Dia de vencimento do depósito <span className="optional-tag">mensal</span></label>
                    <div className="input-with-icon">
                      <Calendar size={16} className="field-icon" />
                      <input type="number" min="1" max="31" className="modal-input-styled" placeholder="Ex: 15 (dia do mês)"
                        value={form.deposit_due_day} onChange={(e) => setForm({ ...form, deposit_due_day: e.target.value })} />
                    </div>
                  </div>
                  <div className="modal-input-group">
                    <label>Limite de crédito (R$) <span className="optional-tag">empréstimo</span></label>
                    <div className="input-with-icon">
                      <Banknote size={16} className="field-icon" />
                      <input type="number" step="0.01" min="0" className="modal-input-styled" placeholder="0,00"
                        value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="modal-input-group" style={{ width: '100%' }}>
                    <label>Modo de saque</label>
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button type="button"
                        style={{
                          flex: 1, padding: '10px 14px', borderRadius: 8, border: '1.5px solid',
                          borderColor: !form.allow_daily_withdrawal ? '#6366f1' : 'rgba(100,100,100,0.2)',
                          background: !form.allow_daily_withdrawal ? 'rgba(99,102,241,0.08)' : 'transparent',
                          color: !form.allow_daily_withdrawal ? '#6366f1' : '#888',
                          fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.2s'
                        }}
                        onClick={() => setForm({ ...form, allow_daily_withdrawal: false })}>
                        <Calendar size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
                        1x por mes (padrao)
                      </button>
                      <button type="button"
                        style={{
                          flex: 1, padding: '10px 14px', borderRadius: 8, border: '1.5px solid',
                          borderColor: form.allow_daily_withdrawal ? '#22c55e' : 'rgba(100,100,100,0.2)',
                          background: form.allow_daily_withdrawal ? 'rgba(34,197,94,0.08)' : 'transparent',
                          color: form.allow_daily_withdrawal ? '#22c55e' : '#888',
                          fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', transition: 'all 0.2s'
                        }}
                        onClick={() => setForm({ ...form, allow_daily_withdrawal: true })}>
                        <Clock size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />
                        1x a cada 24h
                      </button>
                    </div>
                    <span style={{ fontSize: '0.72rem', color: '#888', marginTop: 4, display: 'block' }}>
                      {form.allow_daily_withdrawal ? 'Cliente pode sacar 20% do saldo a cada 24 horas' : 'Cliente pode sacar 20% do saldo 1 vez por mes'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Secao: Observacoes */}
              <div className="form-section">
                <div className="form-section-title">
                  <FileText size={15} />
                  <span>Observacoes <span className="optional-tag">opcional</span></span>
                </div>
                <div className="modal-input-group">
                  <textarea className="modal-textarea" placeholder="Anotacoes sobre o cliente, motivo da quitacao, informacoes adicionais..."
                    rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
              </div>

              {error && <div className="modal-error"><span>{error}</span></div>}

              <button type="submit" className="modal-submit deposit" disabled={loading}>
                {loading ? <div className="spinner-sm" /> : (
                  <>{modal === 'create' ? <><UserPlus size={17} /> Cadastrar usuario</> : <><Edit3 size={17} /> Salvar alteracoes</>}</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
      {/* Import JSON Modal */}
      {importModal && (
        <div className="modal-overlay" onClick={() => { setImportModal(false); setImportError(''); }}>
          <div className="modal-card modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-line" />
            <div className="modal-header">
              <div>
                <h3><FileJson size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />Importar via JSON</h3>
                <p className="modal-subtitle">Importe um usuário com transações ou adicione transações a um usuário existente</p>
              </div>
              <button className="modal-close" onClick={() => { setImportModal(false); setImportError(''); }}><X size={18} /></button>
            </div>

            <div className="modal-form">
              {/* Mode selector */}
              <div className="form-section">
                <div className="form-section-title"><Upload size={15} /><span>Modo de importação</span></div>
                <div className="role-selector">
                  <button type="button" className={`role-option ${importMode === 'new' ? 'active' : ''}`}
                    onClick={() => { setImportMode('new'); setImportError(''); setImportJson(''); }}>
                    <UserPlus size={15} />
                    <div>
                      <span className="role-option-title">Novo usuário</span>
                      <span className="role-option-desc">Criar usuário + transações</span>
                    </div>
                  </button>
                  <button type="button" className={`role-option ${importMode === 'existing' ? 'active' : ''}`}
                    onClick={() => { setImportMode('existing'); setImportError(''); setImportJson(''); }}>
                    <History size={15} />
                    <div>
                      <span className="role-option-title">Usuário existente</span>
                      <span className="role-option-desc">Importar transações apenas</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Select user for existing mode */}
              {importMode === 'existing' && (
                <div className="form-section">
                  <div className="form-section-title"><User size={15} /><span>Selecione o usuário</span></div>
                  <div className="modal-input-group">
                    <select className="modal-input-styled" value={importUserId}
                      onChange={(e) => setImportUserId(e.target.value)}>
                      <option value="">-- Selecione --</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.name} ({u.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')})</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Model template */}
              <div className="form-section">
                <div className="form-section-title">
                  <FileText size={15} />
                  <span>Modelo JSON</span>
                  <button type="button" className="import-toggle-model" onClick={() => setShowModel(!showModel)}>
                    {showModel ? 'Ocultar' : 'Ver modelo'}
                  </button>
                </div>

                {showModel && (
                  <div className="import-model-block">
                    <pre className="import-model-pre">
                      {JSON.stringify(importMode === 'new' ? jsonModel : transactionsOnlyModel, null, 2)}
                    </pre>
                    <div className="import-model-actions">
                      <button type="button" className="import-model-btn" onClick={copyModel}>
                        {modelCopied ? <><Check size={13} /> Copiado!</> : <><Copy size={13} /> Copiar</>}
                      </button>
                      <button type="button" className="import-model-btn" onClick={downloadModel}>
                        <Download size={13} /> Baixar .json
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* File upload */}
              <div className="form-section">
                <div className="form-section-title"><Upload size={15} /><span>Upload do arquivo JSON</span></div>
                <div className="modal-input-group">
                  <label className="import-file-label">
                    <input type="file" accept=".json,application/json" onChange={handleFileUpload} className="import-file-input" />
                    <div className="import-file-drop">
                      <FileJson size={24} />
                      <span>Clique para selecionar um arquivo .json</span>
                      <span className="import-file-hint">ou cole o JSON manualmente abaixo</span>
                    </div>
                  </label>
                </div>

                <div className="modal-input-group">
                  <label>Conteúdo JSON</label>
                  <textarea
                    className="modal-textarea import-json-textarea"
                    rows={8}
                    placeholder='Cole seu JSON aqui ou faça upload do arquivo acima...'
                    value={importJson}
                    onChange={(e) => { setImportJson(e.target.value); setImportError(''); }}
                  />
                </div>
              </div>

              {importError && <div className="modal-error"><AlertTriangle size={14} /><span>{importError}</span></div>}
              {importSuccess && <div className="settings-success" style={{ margin: '0 0 12px' }}><Check size={14} /><span>{importSuccess}</span></div>}

              <button type="button" className="modal-submit deposit" disabled={importLoading || !importJson.trim()}
                onClick={handleImport}>
                {importLoading ? <div className="spinner-sm" /> : <><Upload size={17} /> Importar</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Webhook Modal */}
      {webhookModal && (
        <div className="modal-overlay" onClick={() => setWebhookModal(false)}>
          <div className="modal-card modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-line" style={{ background: '#8b5cf6' }} />
            <div className="modal-header">
              <div>
                <h3><Bell size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />Webhooks de Notificação</h3>
                <p className="modal-subtitle">Receba notificações de depósitos, saques e empréstimos</p>
              </div>
              <button className="modal-close" onClick={() => setWebhookModal(false)}><X size={18} /></button>
            </div>

            <div className="modal-form">
              {/* Add new webhook */}
              <div className="form-section">
                <div className="form-section-title"><Plus size={15} /><span>Novo webhook</span></div>
                <form onSubmit={handleCreateWebhook}>
                  <div className="form-row">
                    <div className="modal-input-group">
                      <label>Nome *</label>
                      <div className="input-with-icon">
                        <Bell size={16} className="field-icon" />
                        <input type="text" className="modal-input-styled" placeholder="Ex: Pushcut, Discord..."
                          value={webhookForm.name} onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })} required />
                      </div>
                    </div>
                    <div className="modal-input-group" style={{ flex: 2 }}>
                      <label>URL *</label>
                      <div className="input-with-icon">
                        <Send size={16} className="field-icon" />
                        <input type="url" className="modal-input-styled" placeholder="https://..."
                          value={webhookForm.url} onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })} required />
                      </div>
                    </div>
                  </div>

                  <div className="modal-input-group" style={{ marginTop: 8 }}>
                    <label>Eventos</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                      {[
                        { key: 'deposit', label: 'Depósitos', color: '#22c55e' },
                        { key: 'withdraw', label: 'Saques', color: '#ef4444' },
                        { key: 'loan', label: 'Empréstimos', color: '#8b5cf6' }
                      ].map(evt => (
                        <button key={evt.key} type="button" onClick={() => toggleWebhookEvent(evt.key)}
                          style={{
                            padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                            border: `1px solid ${webhookForm.events.includes(evt.key) ? evt.color : 'var(--border)'}`,
                            background: webhookForm.events.includes(evt.key) ? `${evt.color}15` : 'transparent',
                            color: webhookForm.events.includes(evt.key) ? evt.color : 'var(--text-muted)',
                            fontFamily: 'Inter, sans-serif'
                          }}>
                          {evt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {webhookError && <div className="modal-error" style={{ marginTop: 8 }}><AlertTriangle size={14} /><span>{webhookError}</span></div>}
                  {webhookSuccess && <div className="settings-success" style={{ margin: '8px 0 0' }}><Check size={14} /><span>{webhookSuccess}</span></div>}

                  <button type="submit" className="schedule-add-btn" disabled={webhookLoading} style={{ background: '#8b5cf6', marginTop: 8 }}>
                    {webhookLoading ? <div className="spinner-sm" /> : <><Plus size={15} /> Adicionar webhook</>}
                  </button>
                </form>
              </div>

              {/* Webhooks list */}
              <div className="form-section">
                <div className="form-section-title"><Bell size={15} /><span>Webhooks configurados ({webhooks.length})</span></div>
                {webhooks.length === 0 ? (
                  <div className="empty-history" style={{ padding: '24px 0' }}>
                    <Bell size={28} className="empty-icon" />
                    <p>Nenhum webhook configurado</p>
                  </div>
                ) : (
                  <div className="schedule-list">
                    {webhooks.map((wh) => (
                      <div key={wh.id} className={`schedule-item ${wh.active ? 'pago' : 'atrasado'}`}>
                        <div className="schedule-status-dot" />
                        <div className="schedule-info" style={{ flex: 1 }}>
                          <span className="schedule-date" style={{ fontWeight: 600 }}>{wh.name}</span>
                          <span className="schedule-desc" style={{ fontSize: 11, wordBreak: 'break-all' }}>{wh.url}</span>
                          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                            {(wh.events || []).map(evt => (
                              <span key={evt} style={{
                                fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                                background: evt === 'deposit' ? 'rgba(34,197,94,0.1)' : evt === 'withdraw' ? 'rgba(239,68,68,0.1)' : 'rgba(139,92,246,0.1)',
                                color: evt === 'deposit' ? '#22c55e' : evt === 'withdraw' ? '#ef4444' : '#8b5cf6'
                              }}>
                                {evt === 'deposit' ? 'Depósito' : evt === 'withdraw' ? 'Saque' : 'Empréstimo'}
                              </span>
                            ))}
                          </div>
                        </div>
                        <span className={`schedule-badge ${wh.active ? 'pago' : 'atrasado'}`}>
                          {wh.active ? 'Ativo' : 'Inativo'}
                        </span>
                        <div className="schedule-actions">
                          <button className="schedule-action-btn" onClick={() => handleTestWebhook(wh)} title="Testar">
                            <Send size={12} />
                          </button>
                          <button className="schedule-action-btn" onClick={() => handleToggleWebhook(wh)}
                            title={wh.active ? 'Desativar' : 'Ativar'}>
                            <Power size={12} />
                          </button>
                          <button className="schedule-action-btn delete" onClick={() => handleDeleteWebhook(wh)} title="Remover">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loan Modal */}
      {loanModal && (
        <div className="modal-overlay" onClick={() => setLoanModal(null)}>
          <div className="modal-card modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-line" style={{ background: '#8b5cf6' }} />
            <div className="modal-header">
              <div>
                <h3><Banknote size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />Empréstimos</h3>
                <p className="modal-subtitle">{loanModal.name} - {loanModal.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                  {loanModal.credit_limit > 0 && <> · Limite: {formatCurrency(loanModal.credit_limit)}</>}
                </p>
              </div>
              <button className="modal-close" onClick={() => setLoanModal(null)}><X size={18} /></button>
            </div>

            <div className="modal-form">
              {/* Create new loan */}
              <div className="form-section">
                <div className="form-section-title"><Plus size={15} /><span>Novo empréstimo</span></div>
                <form onSubmit={handleCreateLoan}>
                  <div className="form-row">
                    <div className="modal-input-group">
                      <label>Valor total (R$) *</label>
                      <div className="input-with-icon">
                        <DollarSign size={16} className="field-icon" />
                        <input type="number" step="0.01" min="0.01" className="modal-input-styled" placeholder="0,00" required
                          value={loanForm.total_amount}
                          onChange={(e) => setLoanForm({ ...loanForm, total_amount: e.target.value })} />
                      </div>
                    </div>
                    <div className="modal-input-group">
                      <label>Juros (%) <span className="optional-tag">mensal</span></label>
                      <div className="input-with-icon">
                        <Percent size={16} className="field-icon" />
                        <input type="number" step="0.01" min="0" className="modal-input-styled" placeholder="0"
                          value={loanForm.interest_rate}
                          onChange={(e) => setLoanForm({ ...loanForm, interest_rate: e.target.value })} />
                      </div>
                    </div>
                    <div className="modal-input-group">
                      <label>Parcelas *</label>
                      <div className="input-with-icon">
                        <Hash size={16} className="field-icon" />
                        <input type="number" min="1" max="360" className="modal-input-styled" placeholder="Ex: 12" required
                          value={loanForm.num_installments}
                          onChange={(e) => setLoanForm({ ...loanForm, num_installments: e.target.value })} />
                      </div>
                    </div>
                  </div>
                  <div className="modal-input-group">
                    <label>Descrição <span className="optional-tag">opcional</span></label>
                    <div className="input-with-icon">
                      <FileText size={16} className="field-icon" />
                      <input type="text" className="modal-input-styled" placeholder="Ex: Empréstimo pessoal"
                        value={loanForm.description}
                        onChange={(e) => setLoanForm({ ...loanForm, description: e.target.value })} />
                    </div>
                  </div>

                  {loanInstallmentPreview && (
                    <div className="rules-banner" style={{ margin: '8px 0', background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)' }}>
                      <Banknote size={16} style={{ color: '#8b5cf6' }} />
                      <span>
                        Total com juros: <strong>{formatCurrency(loanInstallmentPreview.total)}</strong> ·
                        Parcela: <strong>{formatCurrency(loanInstallmentPreview.installment)}</strong>
                      </span>
                    </div>
                  )}

                  {loanError && <div className="modal-error" style={{ marginTop: 8 }}><AlertTriangle size={14} /><span>{loanError}</span></div>}
                  {loanSuccess && <div className="settings-success" style={{ margin: '8px 0 0' }}><Check size={14} /><span>{loanSuccess}</span></div>}

                  <button type="submit" className="schedule-add-btn" disabled={loanLoading} style={{ background: '#8b5cf6', marginTop: 8 }}>
                    {loanLoading ? <div className="spinner-sm" /> : <><Plus size={15} /> Criar empréstimo</>}
                  </button>
                </form>
              </div>

              {/* Loans list */}
              <div className="form-section">
                <div className="form-section-title"><Banknote size={15} /><span>Empréstimos ({loans.length})</span></div>
                {loans.length === 0 ? (
                  <div className="empty-history" style={{ padding: '24px 0' }}>
                    <Banknote size={28} className="empty-icon" />
                    <p>Nenhum empréstimo cadastrado</p>
                  </div>
                ) : (
                  <div className="schedule-list">
                    {loans.map((loan) => {
                      const paidCount = loan.installments ? loan.installments.filter(i => i.status === 'pago').length : 0;
                      const totalCount = loan.installments ? loan.installments.length : 0;
                      const loanProgress = totalCount > 0 ? (paidCount / totalCount) * 100 : 0;
                      const statusColor = loan.status === 'pago' ? '#22c55e' : loan.status === 'atrasado' ? '#ef4444' : loan.status === 'cancelado' ? '#6b7280' : '#8b5cf6';
                      const statusLabel = loan.status === 'pago' ? 'Quitado' : loan.status === 'atrasado' ? 'Atrasado' : loan.status === 'cancelado' ? 'Cancelado' : 'Ativo';

                      return (
                        <div key={loan.id} style={{ marginBottom: 16, border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <div>
                              <strong style={{ fontSize: 14 }}>{formatCurrency(loan.total_amount)}</strong>
                              {loan.interest_rate > 0 && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>+{loan.interest_rate}% juros</span>}
                              {loan.description && <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>· {loan.description}</span>}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span className={`schedule-badge`} style={{ background: `${statusColor}15`, color: statusColor }}>{statusLabel}</span>
                              <button className="schedule-action-btn delete" onClick={() => handleDeleteLoan(loan.id)} title="Remover">
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 12, color: 'var(--text-muted)' }}>
                            <span>Total: {formatCurrency(loan.total_with_interest)}</span>
                            <span>· {paidCount}/{totalCount} parcelas pagas</span>
                            <span>· Parcela: {formatCurrency(loan.installment_amount)}</span>
                          </div>

                          <div className="client-progress-wrap" style={{ marginBottom: 10 }}>
                            <div className="client-progress-bar">
                              <div className="client-progress-fill" style={{ width: `${loanProgress}%`, background: statusColor }} />
                            </div>
                            <span className="client-progress-pct">{loanProgress.toFixed(0)}%</span>
                          </div>

                          {/* Installments */}
                          {loan.installments && loan.installments.length > 0 && (
                            <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                              {loan.installments.map((inst) => {
                                const isPast = new Date(inst.due_date + 'T23:59:59') < new Date() && inst.status !== 'pago';
                                const instClass = inst.status === 'pago' ? 'pago' : isPast ? 'atrasado' : 'pendente';
                                return (
                                  <div key={inst.id} className={`schedule-item ${instClass}`} style={{ padding: '6px 10px' }}>
                                    <div className="schedule-status-dot" />
                                    <div className="schedule-info">
                                      <span className="schedule-date" style={{ fontSize: 12 }}>
                                        Parcela {inst.installment_number} · {new Date(inst.due_date + 'T12:00:00').toLocaleDateString('pt-BR')}
                                      </span>
                                    </div>
                                    <span className="schedule-amount" style={{ fontSize: 12 }}>{formatCurrency(inst.amount)}</span>
                                    <span className={`schedule-badge ${instClass}`} style={{ fontSize: 10, padding: '2px 6px' }}>
                                      {inst.status === 'pago' ? 'Pago' : isPast ? 'Atrasado' : 'Pendente'}
                                    </span>
                                    <div className="schedule-actions">
                                      <button className="schedule-action-btn" onClick={() => handleToggleInstallment(inst)}
                                        title={inst.status === 'pago' ? 'Marcar como pendente' : 'Marcar como pago'}>
                                        {inst.status === 'pago' ? <RotateCcw size={12} /> : <CheckCircle size={12} />}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModal && (
        <div className="modal-overlay" onClick={() => setScheduleModal(null)}>
          <div className="modal-card modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-top-line" />
            <div className="modal-header">
              <div>
                <h3><Calendar size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} />Vencimentos de Depósitos</h3>
                <p className="modal-subtitle">{scheduleModal.name} - {scheduleModal.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}</p>
              </div>
              <button className="modal-close" onClick={() => setScheduleModal(null)}><X size={18} /></button>
            </div>

            <div className="modal-form">
              {/* Add new schedule */}
              <div className="form-section">
                <div className="form-section-title"><Plus size={15} /><span>Novo vencimento</span></div>
                <form onSubmit={handleAddSchedule} className="schedule-add-form">
                  <div className="schedule-add-row">
                    <div className="modal-input-group">
                      <label>Data *</label>
                      <div className="input-with-icon">
                        <Calendar size={16} className="field-icon" />
                        <input type="date" className="modal-input-styled" required
                          value={scheduleForm.due_date}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, due_date: e.target.value })} />
                      </div>
                    </div>
                    <div className="modal-input-group">
                      <label>Valor (R$) *</label>
                      <div className="input-with-icon">
                        <DollarSign size={16} className="field-icon" />
                        <input type="number" step="0.01" min="0.01" className="modal-input-styled" placeholder="0,00" required
                          value={scheduleForm.amount}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, amount: e.target.value })} />
                      </div>
                    </div>
                    <div className="modal-input-group">
                      <label>Descrição</label>
                      <div className="input-with-icon">
                        <FileText size={16} className="field-icon" />
                        <input type="text" className="modal-input-styled" placeholder="Ex: Parcela mensal"
                          value={scheduleForm.description}
                          onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })} />
                      </div>
                    </div>
                  </div>
                  <button type="submit" className="schedule-add-btn" disabled={scheduleLoading}>
                    {scheduleLoading ? <div className="spinner-sm" /> : <><Plus size={15} /> Adicionar</>}
                  </button>
                </form>
                {scheduleError && <div className="modal-error" style={{ marginTop: 8 }}><AlertTriangle size={14} /><span>{scheduleError}</span></div>}
                {scheduleSuccess && <div className="settings-success" style={{ margin: '8px 0 0' }}><Check size={14} /><span>{scheduleSuccess}</span></div>}
              </div>

              {/* Schedules list */}
              <div className="form-section">
                <div className="form-section-title"><Clock size={15} /><span>Vencimentos cadastrados ({schedules.length})</span></div>
                {schedules.length === 0 ? (
                  <div className="empty-history" style={{ padding: '24px 0' }}>
                    <Calendar size={28} className="empty-icon" />
                    <p>Nenhum vencimento cadastrado</p>
                  </div>
                ) : (
                  <div className="schedule-list">
                    {schedules.map((s) => {
                      const isPast = new Date(s.due_date + 'T23:59:59') < new Date() && s.status !== 'pago';
                      const statusClass = s.status === 'pago' ? 'pago' : isPast ? 'atrasado' : 'pendente';
                      return (
                        <div key={s.id} className={`schedule-item ${statusClass}`}>
                          <div className="schedule-status-dot" />
                          <div className="schedule-info">
                            <span className="schedule-date">
                              {new Date(s.due_date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                            {s.description && <span className="schedule-desc">{s.description}</span>}
                          </div>
                          <span className="schedule-amount">{formatCurrency(s.amount)}</span>
                          <span className={`schedule-badge ${statusClass}`}>
                            {s.status === 'pago' ? 'Pago' : isPast ? 'Atrasado' : 'Pendente'}
                          </span>
                          <div className="schedule-actions">
                            <button className="schedule-action-btn" onClick={() => handleToggleScheduleStatus(s)}
                              title={s.status === 'pago' ? 'Marcar como pendente' : 'Marcar como pago'}>
                              {s.status === 'pago' ? <RotateCcw size={13} /> : <CheckCircle size={13} />}
                            </button>
                            <button className="schedule-action-btn delete" onClick={() => handleDeleteSchedule(s)} title="Remover">
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Email Tool Modal */}
      {emailModal && (
        <div className="modal-overlay" onClick={() => setEmailModal(false)}>
          <div className="modal-content" style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title"><Mail size={20} /> Ferramenta de E-mails</h2>
              <button className="modal-close" onClick={() => setEmailModal(false)}><X size={18} /></button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '4px', padding: '0 1.5rem', borderBottom: '1px solid rgba(100,100,100,0.15)' }}>
              {[['compose', 'Compor'], ['templates', 'Templates'], ['history', 'Histórico'], ['stats', 'Estatísticas']].map(([key, label]) => (
                <button key={key} onClick={() => { setEmailTab(key); setEmailError(''); setEmailSuccess(''); if (key === 'history') { setEmailLogsPage(1); loadEmailLogs(1, emailLogsFilter); } if (key === 'templates') loadEmailTemplates(); if (key === 'stats') loadEmailStats(); }}
                  style={{
                    padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.82rem', fontWeight: emailTab === key ? 700 : 400,
                    color: emailTab === key ? '#3b82f6' : 'inherit', borderBottom: emailTab === key ? '2px solid #3b82f6' : '2px solid transparent'
                  }}>{label}</button>
              ))}
            </div>

            <div className="modal-body" style={{ padding: '1.25rem 1.5rem' }}>
              {emailError && <div className="modal-error" style={{ marginBottom: '12px' }}><span>{emailError}</span></div>}
              {emailSuccess && <div style={{ padding: '10px 14px', borderRadius: '8px', background: 'rgba(34,197,94,0.1)', color: '#22c55e', fontSize: '0.82rem', marginBottom: '12px', border: '1px solid rgba(34,197,94,0.2)' }}>{emailSuccess}</div>}

              {/* === COMPOSE TAB === */}
              {emailTab === 'compose' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Mode selector */}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {[['individual', 'Individual'], ['bulk', 'Em massa'], ['reminder', 'Lembrete automático']].map(([key, label]) => (
                      <button key={key} onClick={() => setEmailForm(f => ({ ...f, mode: key }))}
                        style={{
                          padding: '8px 14px', borderRadius: '8px', fontSize: '0.78rem', cursor: 'pointer',
                          border: emailForm.mode === key ? '2px solid #3b82f6' : '1px solid rgba(100,100,100,0.2)',
                          background: emailForm.mode === key ? 'rgba(59,130,246,0.08)' : 'transparent', color: 'inherit', fontWeight: emailForm.mode === key ? 600 : 400
                        }}>{label}</button>
                    ))}
                  </div>

                  {/* Reminder mode info */}
                  {emailForm.mode === 'reminder' && (
                    <div style={{ padding: '12px 14px', borderRadius: '8px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', fontSize: '0.8rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, color: '#f59e0b', marginBottom: '6px' }}>
                        <AlertTriangle size={15} /> {noDepositUsers.length} cliente(s) sem depósito este mês
                      </div>
                      {noDepositUsers.length > 0 && (
                        <div style={{ fontSize: '0.72rem', opacity: 0.8, maxHeight: '80px', overflow: 'auto' }}>
                          {noDepositUsers.map(u => u.name.split(' ')[0]).join(', ')}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Individual recipient */}
                  {emailForm.mode === 'individual' && (
                    <div className="modal-input-group">
                      <label>Destinatário *</label>
                      <select className="modal-input-styled" value={emailForm.user_id} onChange={e => setEmailForm(f => ({ ...f, user_id: e.target.value }))}>
                        <option value="">Selecione um cliente</option>
                        {users.filter(u => u.role === 'usuario' && u.email).map(u => (
                          <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Bulk recipients */}
                  {emailForm.mode === 'bulk' && (
                    <div className="modal-input-group">
                      <label>Destinatários * <span className="optional-tag">{emailForm.user_ids.length} selecionado(s)</span></label>
                      <div style={{ maxHeight: '120px', overflow: 'auto', border: '1px solid rgba(100,100,100,0.2)', borderRadius: '8px', padding: '6px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 6px', fontSize: '0.75rem', fontWeight: 600, borderBottom: '1px solid rgba(100,100,100,0.1)', marginBottom: '4px', cursor: 'pointer' }}>
                          <input type="checkbox"
                            checked={emailForm.user_ids.length === users.filter(u => u.role === 'usuario' && u.email).length}
                            onChange={e => {
                              if (e.target.checked) setEmailForm(f => ({ ...f, user_ids: users.filter(u => u.role === 'usuario' && u.email).map(u => u.id) }));
                              else setEmailForm(f => ({ ...f, user_ids: [] }));
                            }} /> Selecionar todos
                        </label>
                        {users.filter(u => u.role === 'usuario' && u.email).map(u => (
                          <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 6px', fontSize: '0.75rem', cursor: 'pointer' }}>
                            <input type="checkbox" checked={emailForm.user_ids.includes(u.id)}
                              onChange={e => {
                                if (e.target.checked) setEmailForm(f => ({ ...f, user_ids: [...f.user_ids, u.id] }));
                                else setEmailForm(f => ({ ...f, user_ids: f.user_ids.filter(id => id !== u.id) }));
                              }} />
                            {u.name} <span style={{ opacity: 0.5 }}>({u.email})</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Template selector */}
                  <div className="modal-input-group">
                    <label>Template <span className="optional-tag">opcional</span></label>
                    <select className="modal-input-styled" value={emailForm.template_id || ''}
                      onChange={e => {
                        const tpl = emailTemplates.find(t => t.id === parseInt(e.target.value));
                        if (tpl) setEmailForm(f => ({ ...f, template_id: tpl.id, subject: tpl.subject, body_html: tpl.body_html }));
                        else setEmailForm(f => ({ ...f, template_id: null }));
                      }}>
                      <option value="">Personalizado</option>
                      {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name} ({t.category})</option>)}
                    </select>
                  </div>

                  {/* Subject */}
                  <div className="modal-input-group">
                    <label>Assunto {emailForm.mode !== 'reminder' && '*'}</label>
                    <input type="text" className="modal-input-styled" placeholder="Assunto do e-mail"
                      value={emailForm.subject} onChange={e => setEmailForm(f => ({ ...f, subject: e.target.value }))} />
                  </div>

                  {/* Body */}
                  <div className="modal-input-group">
                    <label>Corpo do e-mail {emailForm.mode !== 'reminder' && '*'} <span className="optional-tag">Use {'{{nome}}'} para o nome do cliente</span></label>
                    <textarea className="modal-textarea" rows={6} placeholder="Conteúdo HTML do e-mail..."
                      value={emailForm.body_html} onChange={e => setEmailForm(f => ({ ...f, body_html: e.target.value }))} />
                  </div>

                  <button className="modal-btn-primary" onClick={handleSendEmail} disabled={emailLoading}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    {emailLoading ? <div className="spinner" /> : <><Send size={15} /> {emailForm.mode === 'reminder' ? `Enviar lembrete para ${noDepositUsers.length} cliente(s)` : 'Enviar e-mail'}</>}
                  </button>
                </div>
              )}

              {/* === TEMPLATES TAB === */}
              {emailTab === 'templates' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ padding: '12px', borderRadius: '8px', border: '1px solid rgba(100,100,100,0.15)', background: 'rgba(100,100,100,0.03)' }}>
                    <h4 style={{ margin: '0 0 10px', fontSize: '0.85rem' }}>{emailTemplateForm.editing ? 'Editar template' : 'Novo template'}</h4>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <input type="text" className="modal-input-styled" placeholder="Nome do template" style={{ flex: 2 }}
                        value={emailTemplateForm.name} onChange={e => setEmailTemplateForm(f => ({ ...f, name: e.target.value }))} />
                      <select className="modal-input-styled" style={{ flex: 1 }}
                        value={emailTemplateForm.category} onChange={e => setEmailTemplateForm(f => ({ ...f, category: e.target.value }))}>
                        <option value="geral">Geral</option>
                        <option value="lembrete">Lembrete</option>
                        <option value="cobranca">Cobrança</option>
                        <option value="informativo">Informativo</option>
                      </select>
                    </div>
                    <input type="text" className="modal-input-styled" placeholder="Assunto" style={{ marginBottom: '8px' }}
                      value={emailTemplateForm.subject} onChange={e => setEmailTemplateForm(f => ({ ...f, subject: e.target.value }))} />
                    <textarea className="modal-textarea" rows={4} placeholder="Corpo HTML do template..."
                      value={emailTemplateForm.body_html} onChange={e => setEmailTemplateForm(f => ({ ...f, body_html: e.target.value }))} />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button className="modal-btn-primary" onClick={handleSaveTemplate} disabled={emailLoading} style={{ flex: 1 }}>
                        {emailLoading ? <div className="spinner" /> : <><Check size={14} /> Salvar</>}
                      </button>
                      {emailTemplateForm.editing && (
                        <button className="modal-btn-secondary" onClick={() => setEmailTemplateForm({ name: '', subject: '', body_html: '', category: 'geral', editing: null })}
                          style={{ flex: 0 }}>Cancelar</button>
                      )}
                    </div>
                  </div>

                  {emailTemplates.length === 0 ? (
                    <p style={{ textAlign: 'center', opacity: 0.5, fontSize: '0.82rem' }}>Nenhum template criado</p>
                  ) : emailTemplates.map(t => (
                    <div key={t.id} style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(100,100,100,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>{t.name}</span>
                        <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '4px', fontSize: '0.68rem', background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>{t.category}</span>
                        <div style={{ fontSize: '0.72rem', opacity: 0.6, marginTop: '2px' }}>{t.subject}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="table-edit-btn" onClick={() => setEmailTemplateForm({ name: t.name, subject: t.subject, body_html: t.body_html, category: t.category, editing: t.id })}>
                          <Edit3 size={13} />
                        </button>
                        <button className="table-edit-btn" style={{ color: '#ef4444' }} onClick={() => handleDeleteTemplate(t.id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* === HISTORY TAB === */}
              {emailTab === 'history' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {[['', 'Todos'], ['sent', 'Enviados'], ['opened', 'Abertos'], ['clicked', 'Clicados'], ['failed', 'Falhas']].map(([key, label]) => (
                      <button key={key} onClick={() => { setEmailLogsFilter(key); setEmailLogsPage(1); loadEmailLogs(1, key); }}
                        style={{
                          padding: '6px 12px', borderRadius: '6px', fontSize: '0.75rem', cursor: 'pointer',
                          border: emailLogsFilter === key ? '2px solid #3b82f6' : '1px solid rgba(100,100,100,0.2)',
                          background: emailLogsFilter === key ? 'rgba(59,130,246,0.08)' : 'transparent', color: 'inherit'
                        }}>{label}</button>
                    ))}
                  </div>
                  {emailLogs.logs.length === 0 ? (
                    <p style={{ textAlign: 'center', opacity: 0.5, fontSize: '0.82rem' }}>Nenhum e-mail enviado</p>
                  ) : (
                    <>
                      {emailLogs.logs.map(log => {
                        const statusConfig = {
                          clicked: { bg: 'rgba(59,130,246,0.1)', color: '#3b82f6', label: 'Clicou' },
                          opened: { bg: 'rgba(34,197,94,0.1)', color: '#22c55e', label: 'Aberto' },
                          failed: { bg: 'rgba(239,68,68,0.1)', color: '#ef4444', label: 'Falha' },
                          sent: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', label: 'Enviado' }
                        };
                        const st = statusConfig[log.status] || statusConfig.sent;
                        return (
                        <div key={log.id} style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid rgba(100,100,100,0.15)', fontSize: '0.8rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontWeight: 600 }}>{log.user_name || 'Usuário removido'}</span>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              {log.click_count > 0 && (
                                <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 600, background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
                                  {log.click_count} clique{log.click_count > 1 ? 's' : ''}
                                </span>
                              )}
                              <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 600, background: st.bg, color: st.color }}>{st.label}</span>
                            </div>
                          </div>
                          <div style={{ opacity: 0.7, fontSize: '0.75rem' }}>{log.subject}</div>
                          <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '0.7rem', opacity: 0.5, flexWrap: 'wrap' }}>
                            <span>Enviado: {new Date(log.created_at).toLocaleString('pt-BR')}</span>
                            {log.opened_at && <span>Aberto: {new Date(log.opened_at).toLocaleString('pt-BR')}</span>}
                            {log.clicked_at && <span>Clicou: {new Date(log.clicked_at).toLocaleString('pt-BR')}</span>}
                          </div>
                        </div>
                        );
                      })}
                      {emailLogs.total > 20 && (
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', paddingTop: '8px' }}>
                          <button disabled={emailLogsPage <= 1} onClick={() => { const p = emailLogsPage - 1; setEmailLogsPage(p); loadEmailLogs(p, emailLogsFilter); }}
                            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid rgba(100,100,100,0.2)', background: 'transparent', cursor: 'pointer', color: 'inherit', opacity: emailLogsPage <= 1 ? 0.4 : 1 }}>{'<'}</button>
                          <span style={{ padding: '6px 0', fontSize: '0.78rem' }}>Página {emailLogsPage} de {Math.ceil(emailLogs.total / 20)}</span>
                          <button disabled={emailLogsPage >= Math.ceil(emailLogs.total / 20)} onClick={() => { const p = emailLogsPage + 1; setEmailLogsPage(p); loadEmailLogs(p, emailLogsFilter); }}
                            style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid rgba(100,100,100,0.2)', background: 'transparent', cursor: 'pointer', color: 'inherit', opacity: emailLogsPage >= Math.ceil(emailLogs.total / 20) ? 0.4 : 1 }}>{'>'}</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* === STATS TAB === */}
              {emailTab === 'stats' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {!emailStats ? (
                    <p style={{ textAlign: 'center', opacity: 0.5, fontSize: '0.82rem', padding: '20px 0' }}>Carregando...</p>
                  ) : (
                    <>
                      {/* Summary cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
                        {[
                          { label: 'Total enviados', value: emailStats.summary.total, color: '#6b7280' },
                          { label: 'Abertos', value: parseInt(emailStats.summary.opened) + parseInt(emailStats.summary.clicked), color: '#22c55e' },
                          { label: 'Clicaram', value: emailStats.summary.clicked, color: '#3b82f6' },
                          { label: 'Falhas', value: emailStats.summary.failed, color: '#ef4444' },
                        ].map((item, i) => (
                          <div key={i} style={{ padding: '14px 12px', borderRadius: '10px', border: '1px solid rgba(100,100,100,0.15)', textAlign: 'center' }}>
                            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: item.color }}>{item.value}</div>
                            <div style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '2px' }}>{item.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* Rates */}
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1, padding: '14px', borderRadius: '10px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#22c55e' }}>{emailStats.summary.open_rate || 0}%</div>
                          <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>Taxa de abertura</div>
                        </div>
                        <div style={{ flex: 1, padding: '14px', borderRadius: '10px', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#3b82f6' }}>{emailStats.summary.click_rate || 0}%</div>
                          <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>Taxa de cliques</div>
                        </div>
                      </div>

                      {/* Daily breakdown */}
                      {emailStats.daily.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', fontWeight: 600 }}>Últimos 7 dias</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {emailStats.daily.map((day, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '6px', border: '1px solid rgba(100,100,100,0.1)', fontSize: '0.78rem' }}>
                                <span style={{ minWidth: '80px', fontWeight: 500 }}>{new Date(day.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                                <span style={{ opacity: 0.7 }}>{day.total} enviado{day.total > 1 ? 's' : ''}</span>
                                <span style={{ color: '#22c55e' }}>{day.opens} aberto{day.opens > 1 ? 's' : ''}</span>
                                <span style={{ color: '#3b82f6' }}>{day.clicks} clique{day.clicks > 1 ? 's' : ''}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Top clicked links */}
                      {emailStats.topLinks.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', fontWeight: 600 }}>Links mais clicados (30 dias)</h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {emailStats.topLinks.map((link, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: '6px', border: '1px solid rgba(100,100,100,0.1)', fontSize: '0.75rem' }}>
                                <span style={{ opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '400px' }}>{link.original_url}</span>
                                <span style={{ fontWeight: 600, color: '#3b82f6', whiteSpace: 'nowrap', marginLeft: '8px' }}>{link.clicks}x</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Email History Modal */}
      {userEmailHistory.show && (
        <div className="modal-overlay" onClick={() => setUserEmailHistory({ userId: null, logs: [], show: false })}>
          <div className="modal-content" style={{ maxWidth: '550px', maxHeight: '80vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title"><Mail size={20} /> Histórico de e-mails</h2>
              <button className="modal-close" onClick={() => setUserEmailHistory({ userId: null, logs: [], show: false })}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ padding: '1rem 1.5rem' }}>
              {userEmailHistory.logs.length === 0 ? (
                <p style={{ textAlign: 'center', opacity: 0.5, fontSize: '0.82rem', padding: '20px 0' }}>Nenhum e-mail enviado para este cliente</p>
              ) : userEmailHistory.logs.map(log => {
                const stCfg = {
                  clicked: { bg: 'rgba(59,130,246,0.1)', color: '#3b82f6', label: 'Clicou' },
                  opened: { bg: 'rgba(34,197,94,0.1)', color: '#22c55e', label: 'Aberto' },
                  failed: { bg: 'rgba(239,68,68,0.1)', color: '#ef4444', label: 'Falha' },
                  sent: { bg: 'rgba(245,158,11,0.1)', color: '#f59e0b', label: 'Enviado' }
                };
                const s = stCfg[log.status] || stCfg.sent;
                return (
                <div key={log.id} style={{ padding: '12px', borderRadius: '8px', border: '1px solid rgba(100,100,100,0.15)', marginBottom: '8px', fontSize: '0.8rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontWeight: 600 }}>{log.subject}</span>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      {log.click_count > 0 && (
                        <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 600, background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
                          {log.click_count} clique{log.click_count > 1 ? 's' : ''}
                        </span>
                      )}
                      <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.68rem', fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '0.72rem', opacity: 0.6, flexWrap: 'wrap' }}>
                    <span><Clock size={11} /> {new Date(log.created_at).toLocaleString('pt-BR')}</span>
                    {log.opened_at && <span><Eye size={11} /> Aberto em {new Date(log.opened_at).toLocaleString('pt-BR')}</span>}
                    {log.clicked_at && <span>Clicou em {new Date(log.clicked_at).toLocaleString('pt-BR')}</span>}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
